// CourseStatusButton.tsx
// The "change status" control on a course card. Renders the course's current
// status as a colored pill with a meaningful icon + label, and opens the status
// modal on click. Replaces the old dot-in-a-box control (which read like a
// veg / non-veg indicator).
import React from 'react';
import {
  CheckCircle2,
  EyeOff,
  Clock,
  PencilLine,
  XCircle,
  Archive,
  Settings2,
  type LucideIcon,
} from 'lucide-react';
import type { CourseDisplayStatus } from './courseStatus';
import './CourseStatusButton.css';

interface StatusVisual {
  Icon: LucideIcon;
  color: string;
  bg: string;
}

const STATUS_VISUALS: Record<CourseDisplayStatus, StatusVisual> = {
  Active:   { Icon: CheckCircle2, color: '#00A045', bg: '#d1fae5' },
  Inactive: { Icon: EyeOff,       color: '#EF4444', bg: '#fee2e2' },
  Pending:  { Icon: Clock,        color: '#CA8A04', bg: '#fef9c3' },
  Draft:    { Icon: PencilLine,   color: '#6B7280', bg: '#f3f4f6' },
  Rejected: { Icon: XCircle,      color: '#EF4444', bg: '#fee2e2' },
  Archived: { Icon: Archive,      color: '#6B7280', bg: '#f3f4f6' },
};

interface CourseStatusButtonProps {
  status: CourseDisplayStatus;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const CourseStatusButton: React.FC<CourseStatusButtonProps> = ({ status, onClick }) => {
  const { Icon, color, bg } = STATUS_VISUALS[status] ?? STATUS_VISUALS.Draft;

  return (
    <button
      type="button"
      className="course-status-btn"
      onClick={onClick}
      title={`Status: ${status} — click to change`}
      aria-label={`Change status, currently ${status}`}
      style={{ color, backgroundColor: bg, borderColor: color }}
    >
      <Icon size={14} className="course-status-btn-icon" />
      <span className="course-status-btn-label">{status}</span>
      <Settings2 size={13} className="course-status-btn-affordance" />
    </button>
  );
};

export default CourseStatusButton;
